import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { adminAuth, STATUS_LABELS, euros } from "@/lib/ceda";

const EMPTY = { name: "", description: "", size: 3, capsule_ids: [], status: "draft" };

export const AdminCedaPacks = ({ capsules }) => {
  const [packs, setPacks] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/ceda/packs", adminAuth()).then((r) => setPacks(r.data));
  useEffect(() => { load(); }, []);
  const label = (id) => { const c = capsules.find((x) => x.id === id); return c ? `${c.code} — ${c.title}` : "(capsule supprimée)"; };

  const save = async () => {
    try {
      const body = { ...edit, size: Number(edit.size) };
      if (edit.id) await api.put(`/admin/ceda/packs/${edit.id}`, body, adminAuth());
      else await api.post("/admin/ceda/packs", body, adminAuth());
      toast.success("Pack enregistré"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Supprimer le pack ${p.name} ?`)) return;
    await api.delete(`/admin/ceda/packs/${p.id}`, adminAuth()); load();
  };
  const toggleCap = (id) => setEdit((e) => ({ ...e, capsule_ids: e.capsule_ids.includes(id) ? e.capsule_ids.filter((x) => x !== id) : e.capsule_ids.length < Number(e.size) ? [...e.capsule_ids, id] : e.capsule_ids }));

  return (
    <section data-testid="admin-ceda-packs">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-serif text-2xl text-[#1F2A24]">Packs fixes</h3>
        <button data-testid="admin-ceda-add-pack" onClick={() => setEdit(EMPTY)} className="inline-flex items-center gap-2 rounded-full border border-[#385E3A] text-[#385E3A] px-4 py-2 font-ui text-sm"><Plus size={15} /> Pack</button>
      </div>
      <p className="font-sans text-xs text-[#1F2A24]/55 mb-4">En plus des packs fixes, les clients peuvent toujours composer librement leur pack de 3 ou de 7 (toutes familles ou une seule).</p>
      <div className="space-y-3">
        {packs.map((p) => (
          <div key={p.id} className="rounded-xl bg-white border border-[#E8DCC4] p-4 flex flex-wrap justify-between gap-3" data-testid={`admin-ceda-pack-${p.id}`}>
            <div>
              <p className="font-serif text-lg text-[#1F2A24]">{p.name} <span className="font-ui text-xs text-[#C9A24B]">· {p.size} capsules · {euros(p.price_cents)} · {STATUS_LABELS[p.status]}</span></p>
              <p className="font-sans text-xs text-[#1F2A24]/60 mt-1">{p.capsule_ids.map(label).join(" · ") || "aucune capsule"}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEdit(p)} className="p-1.5 rounded border" data-testid={`admin-ceda-pack-edit-${p.id}`}><Pencil size={13} /></button>
              <button onClick={() => del(p)} className="p-1.5 rounded border text-[#A63B36]"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      {edit && (
        <div className="mt-4 rounded-xl bg-white border-2 border-[#C9A24B] p-4 space-y-3" data-testid="admin-ceda-pack-form">
          <div className="grid sm:grid-cols-3 gap-2">
            <input className="border rounded px-3 py-2 text-sm" placeholder="Nom du pack" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="admin-ceda-pack-name" />
            <select className="border rounded px-3 py-2 text-sm bg-white" value={edit.size} onChange={(e) => setEdit({ ...edit, size: Number(e.target.value), capsule_ids: [] })} data-testid="admin-ceda-pack-size"><option value={3}>Pack de 3</option><option value={7}>Pack de 7</option></select>
            <select className="border rounded px-3 py-2 text-sm bg-white" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="admin-ceda-pack-status">{Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </div>
          <textarea className="border rounded px-3 py-2 text-sm w-full" rows={2} placeholder="Description" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
          <p className="font-sans text-xs text-[#1F2A24]/60">{edit.capsule_ids.length} / {edit.size} capsules</p>
          <div className="flex flex-wrap gap-2">
            {capsules.map((c) => (
              <button key={c.id} onClick={() => toggleCap(c.id)} data-testid={`admin-ceda-pack-cap-${c.id}`} className={`rounded-full px-3 py-1.5 text-xs border ${edit.capsule_ids.includes(c.id) ? "bg-[#C9A24B] border-[#C9A24B]" : "border-[#E8DCC4]"}`}>{c.code || c.title}{c.status !== "published" ? " (non publiée)" : ""}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={save} data-testid="admin-ceda-pack-save" className="rounded-full bg-[#385E3A] text-white px-5 py-2 text-sm">Enregistrer</button>
            <button onClick={() => setEdit(null)} className="rounded-full border px-5 py-2 text-sm">Annuler</button>
          </div>
        </div>
      )}
    </section>
  );
};
